/* eslint-disable max-lines -- all part of menu builder */
import { G } from '@mobily/ts-belt';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Card, CardHeader, Collapse, Popover, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { createStyles, makeStyles } from '@mui/styles';
import {
  MultiLevelMenu,
  useAppLocationMatcher,
  useMultiLevelMenu,
} from '@react-admin/ra-navigation';
import { canAccess, Permissions } from '@react-admin/ra-rbac';
import {
  ComponentType,
  cloneElement,
  JSX,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useGetIdentity,
  useLocaleState,
  usePermissions,
  useSidebarState,
  useTranslate,
} from 'react-admin';
import { stringify } from 'safe-stable-stringify';

import { BOResourceAll } from '@Common/types/bo_external/resources';

import { AppVersion } from '@sh4res/bo-common/components/AppVersion';
import { Avatar } from '@sh4res/bo-common/components/Avatar';
import { ProfilePopup } from '@sh4res/bo-common/components/ProfilePopup';
import { UTC_OPTIONS } from '@sh4res/bo-common/fields';
import { getTheme } from '@sh4res/bo-common/theme';
import { Resources } from '@sh4res/bo-common/utils';
import { isBoExternalIdentity } from '@sh4res/bo-common/utils/b2b_auth_role';
import { buildLink } from '@sh4res/bo-common/utils/build_link';

export {
  LocationContext as CommonLocationContext,
  useDefineAppLocation,
} from '@react-admin/ra-navigation';

export type SingleLevelItem<TTranslation extends string> = {
  name: BOResourceAll;
  path?: string;
  icon: JSX.Element;
  label: TTranslation;
  filter?: Record<string, unknown>; // add query types per resource
  action?: 'list' | 'show';
};

type SingleCustomRouteItem<TTranslation extends string> = Omit<
  SingleLevelItem<TTranslation>,
  'name' | 'path'
> & {
  /**
   * the resource part of the name is used for permission checking
   */
  name: BOResourceAll | `${BOResourceAll}.${string}`;
  path: string;
  customRoute: true;
};

type MultiLevelItem<TTranslation extends string> = {
  name: string;
  label: string;
  icon: JSX.Element;
  items: readonly (
    | SingleLevelItem<TTranslation>
    | SingleCustomRouteItem<TTranslation>
    | MultiLevelItem<TTranslation>
  )[];
};

export type MenuItemsItem<TTranslation extends string> =
  | SingleLevelItem<TTranslation>
  | SingleCustomRouteItem<TTranslation>
  | MultiLevelItem<TTranslation>;

function UTCTime() {
  const [date, setDate] = useState(() => new Date());

  useEffect(() => {
    // refresh time
    const interval = setInterval(() => {
      setDate(new Date());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <Typography
      sx={{
        color: 'rgba(255, 255, 255, 0.4)',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '16px',
        margin: 0,
      }}>
      {date.toLocaleString(undefined, {
        ...UTC_OPTIONS,
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })}
    </Typography>
  );
}

const ContactSupportMenuItem = () => {
  const translate = useTranslate();
  const translatedLabel = translate('profile_popup.contact_support');
  const classes = useStyles();
  const [sidebarIsOpen] = useSidebarState();

  return (
    <MultiLevelMenu.Item
      name="contact_support"
      to="/contact_support"
      label={translatedLabel}
      icon={
        <Tooltip title={translatedLabel} placement="right" disableHoverListener={sidebarIsOpen}>
          <ContactSupportIcon aria-hidden={true} style={{ width: '22px', height: '22px' }} />
        </Tooltip>
      }
      className={`${classes.menuItem}`}
    />
  );
};

const User = ({
  onAvatarClick,
}: {
  onAvatarClick: (event: SyntheticEvent<HTMLDivElement>) => void;
}) => {
  const [sidebarIsOpen] = useSidebarState();
  const { data } = useGetIdentity();
  if (data === undefined) {
    return null;
  }

  return (
    <div
      data-tour-id="profile-popup"
      role="button"
      style={{
        height: '60px',
        cursor: 'pointer',
      }}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onAvatarClick(event);
        }
      }}
      onClick={onAvatarClick}>
      {sidebarIsOpen ? (
        <Card
          sx={{
            padding: 0,
          }}>
          <CardHeader
            sx={{ padding: '14px 12px 14px 12px', '.MuiCardHeader-avatar': { marginRight: '5px' } }}
            avatar={
              <Avatar
                sx={{ width: 40, height: 40 }}
                src={isBoExternalIdentity(data) ? undefined : data.picture_url}
                fullName={data.fullName}
              />
            }
            title={
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '17px',
                  lineHeight: '22px',
                  whiteSpace: 'nowrap',
                  maxWidth: '165px',
                  overflowX: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {data.fullName}
              </Typography>
            }
            subheader={
              <>
                <UTCTime />
                <AppVersion />
              </>
            }
          />
        </Card>
      ) : (
        <Avatar
          sx={{ width: 40, height: 40, margin: '24px 0px 0px 5px' }}
          src={isBoExternalIdentity(data) ? undefined : data.picture_url}
          fullName={data.fullName}
        />
      )}
    </div>
  );
};

export function MenuBuilder<TTranslation extends string>({
  menuItems,
  showUserProfile = false,
  showContactSupport = false,
  hideLogoutButton = false,
  showProductTour = false,
  startTourHandle,
  companySelector,
  header,
  SettingsDialog,
}: {
  menuItems: readonly MenuItemsItem<TTranslation>[];
  showUserProfile?: boolean;
  showContactSupport?: boolean;
  hideLogoutButton?: boolean;
  showProductTour?: boolean;
  startTourHandle?: () => void;
  companySelector?: ReactElement;
  /** rendered above the menu entries, below the company selector */
  header?: ReactElement;
  /** adds a "Settings" entry to the profile popup that opens this dialog */
  SettingsDialog?: ComponentType<{ open: boolean; onClose: () => void }>;
}) {
  const { isLoading, permissions } = usePermissions<Permissions>();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const [locale] = useLocaleState();
  useEffect(() => {
    // Update the html lang attribute
    document.documentElement.lang = locale;
  }, [locale]);

  const openPopover = useCallback(
    (event: SyntheticEvent<HTMLDivElement>) => setAnchorEl(event.currentTarget),
    [setAnchorEl],
  );

  const closePopover = useCallback(() => setAnchorEl(null), [setAnchorEl]);
  const closeSettings = useCallback(() => setIsSettingsOpen(false), []);

  if (isLoading || G.isNullable(permissions)) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(var(--app-height) - 60px)',
        paddingBottom: '20px',
        maxWidth: '240px',
      }}>
      {companySelector}
      {header}
      <Box
        data-tour-id="main-menu"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}>
        <MultiLevelMenu aria-label="main menu">
          {menuItems.map((menuItem) => (
            <MultiLevelMenuItem
              key={`MultiLevelMenuItem-${menuItem.name}`}
              item={menuItem}
              permissions={permissions}
            />
          ))}
        </MultiLevelMenu>
      </Box>

      <Box>
        <MultiLevelMenu sx={{ minHeight: 0 }}>
          {showContactSupport && <ContactSupportMenuItem />}
        </MultiLevelMenu>
        {showUserProfile && <User onAvatarClick={openPopover} />}
      </Box>

      <Popover
        id="profile-popover"
        aria-labelledby="profile-popover"
        open={isOpen}
        onClose={closePopover}
        anchorEl={anchorEl}
        style={{
          marginLeft: '-20px',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: {
              borderRadius: '14px',
            },
          },
        }}>
        <ProfilePopup
          hideLogoutButton={hideLogoutButton}
          showProductTour={showProductTour}
          startTourHandle={() => {
            closePopover();
            if (startTourHandle) {
              startTourHandle();
            }
          }}
          onOpenSettings={
            SettingsDialog
              ? () => {
                  closePopover();
                  setIsSettingsOpen(true);
                }
              : undefined
          }
        />
      </Popover>
      {/* outside the Popover: it unmounts its content when closed */}
      {SettingsDialog && <SettingsDialog open={isSettingsOpen} onClose={closeSettings} />}
    </Box>
  );
}

// to see the menu item with the menuName a special permission needs to be added
// for example only users with bo_users/bulk_toggle_confidential can see bo_users list menu item
const SPECIAL_PERMISSIONS: Record<string, Permissions> = {
  bo_users: [{ resource: 'bo_users/bulk_toggle_confidential', action: 'create' }],
};

function hasSpecialPermissionsDefined(
  specialPermissionForItem: Permissions,
  userPermissions: Permissions,
) {
  return specialPermissionForItem.some((required) =>
    userPermissions.some(
      (user) =>
        (user.resource === required.resource || user.resource === '*') &&
        (user.action === required.action || user.action === '*'),
    ),
  );
}
function passesSpecialPermissions(name: string, permissions: Permissions) {
  const specialPermissionForItem = SPECIAL_PERMISSIONS[name];
  return (
    G.isNullable(specialPermissionForItem) ||
    hasSpecialPermissionsDefined(specialPermissionForItem, permissions)
  );
}

/**
 * Whether the menu shows a leaf item (or would link to it) for the given permissions
 */
export function isMenuLeafVisible(
  { name, action = 'list' }: { name: string; action?: 'list' | 'show' },
  permissions: Permissions,
) {
  return (
    passesSpecialPermissions(name, permissions) &&
    canAccess({
      permissions,
      // menu item names can also contain `.` followed by anything
      resource: name.split('.')[0],
      action,
    })
  );
}

/**
 * Where a menu leaf links to: its custom route or the resource list, with the item's default filter
 */
export function getMenuLeafLink({
  name,
  path,
  filter,
}: {
  name: string;
  path?: string;
  filter?: Record<string, unknown>;
}) {
  return {
    pathname: path ? path : buildLink(name as Resources, 'list'),
    ...(filter && { search: new URLSearchParams({ filter: stringify(filter) }).toString() }),
  };
}

function MultiLevelMenuItem<TTranslation extends string>({
  item,
  permissions,
}: {
  item: MenuItemsItem<TTranslation>;
  permissions: Permissions;
}) {
  if (!passesSpecialPermissions(item.name, permissions)) {
    return null;
  }

  if ('items' in item) {
    return (
      <MultiMenuItem
        name={item.name}
        label={item.label}
        icon={item.icon}
        permissions={permissions}
        items={item.items}
      />
    );
  }

  return (
    <MenuItem
      name={item.name}
      path={item.path}
      label={item.label}
      icon={item.icon}
      filter={item.filter}
      action={item.action}
      permissions={permissions}
      data-testid="BoExternalMenuBuilder_MenuItem"
    />
  );
}

interface MultiMenuItemProps<TTranslation extends string> {
  name: string;
  permissions: Permissions;
  icon?: JSX.Element;
  label: string;
  items: readonly (
    | SingleLevelItem<TTranslation>
    | SingleCustomRouteItem<TTranslation>
    | MultiLevelItem<TTranslation>
  )[];
}
function MultiMenuItem<TTranslation extends string>({
  icon,
  items,
  label,
  name,
  permissions,
}: MultiMenuItemProps<TTranslation>) {
  const multiLevelMenu = useMultiLevelMenu();
  const [sidebarIsOpen] = useSidebarState();
  const translate = useTranslate();
  const match = useAppLocationMatcher();

  const isActive = useMemo(() => !!match(name), [match, name]);
  const [isOpen, setIsOpenState] = useState(isActive || multiLevelMenu.isOpen(name));
  const hasItemsWithAccess = useMemo(() => {
    const hasAccessDeep = (menuItem: MenuItemsItem<TTranslation>): boolean => {
      if ('items' in menuItem) {
        return menuItem.items.some((child) => hasAccessDeep(child));
      }
      return canAccess({
        permissions,
        // menu item names can also contain `.` followed by anything
        resource: menuItem.name.split('.')[0],
        action: menuItem.action ?? 'list',
      });
    };
    return items.some((subItem) => hasAccessDeep(subItem));
  }, [items, permissions]);

  const translatedLabel = useMemo(() => translate(label), [label, translate]);

  const handleToggle = useCallback(
    (event?: React.SyntheticEvent) => {
      event?.preventDefault();
      multiLevelMenu.toggle(name);
      setIsOpenState((prev) => !prev);
    },
    [multiLevelMenu, name],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleToggle(event);
      }
    },
    [handleToggle],
  );
  return !hasItemsWithAccess ? null : (
    <Box>
      <Box
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        sx={(theme) => ({
          color: getTheme() === 'axa' ? '#000000' : '#808080',
          cursor: 'pointer',
          px: theme.spacing(1),
          mx: theme.spacing(1),
          height: '40px',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&:focus-visible': {
            backgroundColor: theme.palette.action.focus,
            outline: 'none',
          },
        })}
        tabIndex={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: !sidebarIsOpen ? undefined : '33px',
              }}>
              <Tooltip
                title={translatedLabel}
                placement="right"
                disableHoverListener={sidebarIsOpen}>
                {sidebarIsOpen || !isOpen ? (
                  cloneElement(icon, {
                    titleAccess: translatedLabel,
                  })
                ) : (
                  <ExpandMoreIcon style={{ width: '22px', height: '22px' }} />
                )}
              </Tooltip>
            </Box>
          )}
          {sidebarIsOpen && (
            <Typography
              sx={{
                flexGrow: 1,
                fontWeight: 500,
                fontSize: '15.5px',
                color: 'inherit !important',
              }}>
              {translatedLabel}
            </Typography>
          )}
          {sidebarIsOpen && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isOpen ? (
                <ExpandLessIcon style={{ width: '22px', height: '22px' }} />
              ) : (
                <ExpandMoreIcon style={{ width: '22px', height: '22px' }} />
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Collapse
        in={isOpen}
        unmountOnExit
        sx={(theme) => ({
          paddingLeft: sidebarIsOpen ? theme.spacing(2) : undefined,
          transition: 'all .1s ease',
        })}>
        {items.map((subItem) => {
          const filterKey = 'items' in subItem ? '' : stringify(subItem.filter ?? {});
          return (
            <MultiLevelMenuItem
              key={`MultiMenuItem-${name}-${subItem.name}-${filterKey}`}
              permissions={permissions}
              item={subItem}
            />
          );
        })}
      </Collapse>
    </Box>
  );
}

interface MenuItemProps<TTranslation extends string> {
  name: string;
  path?: string;
  icon?: JSX.Element;
  permissions: Permissions;
  filter?: Record<string, unknown>;
  label: TTranslation;
  action?: 'show' | 'list';
  'data-testid'?: string;
}
function MenuItem<TTranslation extends string>({
  name,
  path,
  icon,
  permissions,
  filter,
  label,
  action = 'list',
  'data-testid': dataTestId,
}: MenuItemProps<TTranslation>) {
  const classes = useStyles();
  const [sidebarIsOpen] = useSidebarState();
  const translate = useTranslate();
  const match = useAppLocationMatcher();
  const isActive = useMemo(() => !!match(name), [match, name]);

  const canAccessItem = isMenuLeafVisible({ name, action }, permissions);

  const translatedLabel = useMemo(
    // stricter translate typing does not work well with generics
    () => translate(label as Parameters<typeof translate>[0]),
    [label, translate],
  );

  if (!canAccessItem) {
    return null;
  }

  return (
    <MultiLevelMenu.Item
      name={name}
      to={getMenuLeafLink({ name, path, filter })}
      data-testid={dataTestId}
      label={translatedLabel}
      aria-current={isActive ? 'page' : undefined}
      icon={
        icon ? (
          <Tooltip title={translatedLabel} placement="right" disableHoverListener={sidebarIsOpen}>
            {icon}
          </Tooltip>
        ) : undefined
      }
      className={`${classes.menuItem} ${icon ? '' : classes.subMenuItem}`}
    />
  );
}

const useStyles = makeStyles(() =>
  createStyles({
    menuItem: {
      '& .MuiCollapse-wrapper': {
        width: '100%',
      },
      '& .RaMenuItem-link': {
        width: '100%',
      },
    },
    subMenuItem: {
      marginLeft: '10px',
    },
  }),
);
